// api/funnelApi.js
// Funciones para tracking y consulta de métricas del funnel de onboarding

import axios from "axios"

const API_BASE = window.REACT_APP_URL || ""
const BASE_PATH = `${API_BASE}/ewanapi/v1/onboarding/funnel`

// ============================================
// Gestión de sesión
// ============================================

/**
 * Genera o recupera un ID de sesión único
 */
export const obtenerSesionId = () => {
  let sesionId = sessionStorage.getItem("onboarding_session_id")
  if (!sesionId) {
    sesionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    sessionStorage.setItem("onboarding_session_id", sesionId)
  }
  return sesionId
}

/**
 * Limpia la sesión actual
 */
export const limpiarSesion = () => {
  sessionStorage.removeItem("onboarding_session_id")
  sessionStorage.removeItem("onboarding_last_event_time")
}

// ============================================
// Registro de eventos
// ============================================

/**
 * Tipos de eventos del funnel
 */
export const TIPOS_EVENTO = {
  // Inicio y fin
  INICIO_ONBOARDING: "INICIO_ONBOARDING",
  FIN_EXITOSO: "FIN_EXITOSO",
  ABANDONO: "ABANDONO",

  // Navegación
  PASO_INICIADO: "PASO_INICIADO",
  PASO_COMPLETADO: "PASO_COMPLETADO",
  PASO_RETROCESO: "PASO_RETROCESO",

  // Eventos específicos
  CUIT_BUSCADO: "CUIT_BUSCADO",
  CUIT_ENCONTRADO: "CUIT_ENCONTRADO",
  CUIT_NO_ENCONTRADO: "CUIT_NO_ENCONTRADO",

  DOCUMENTO_CARGADO: "DOCUMENTO_CARGADO",
  DOCUMENTO_RECHAZADO: "DOCUMENTO_RECHAZADO",

  KYC_INICIADO: "KYC_INICIADO",
  KYC_COMPLETADO: "KYC_COMPLETADO",
  KYC_ALERTA: "KYC_ALERTA",

  NOSIS_CONSULTADO: "NOSIS_CONSULTADO",
  NOSIS_ALERTA: "NOSIS_ALERTA",

  EVALUACION_INICIADA: "EVALUACION_INICIADA",
  EVALUACION_COMPLETADA: "EVALUACION_COMPLETADA",
  EVALUACION_RECHAZADA: "EVALUACION_RECHAZADA",

  ESTATUTO_CARGADO: "ESTATUTO_CARGADO",
  ESTATUTO_EXTRAIDO: "ESTATUTO_EXTRAIDO",
  FIRMANTE_AGREGADO: "FIRMANTE_AGREGADO",
  BIOMETRIA_INICIADA: "BIOMETRIA_INICIADA",
  BIOMETRIA_COMPLETADA: "BIOMETRIA_COMPLETADA",
  BIOMETRIA_RECHAZADA: "BIOMETRIA_RECHAZADA",

  GARANTIA_AGREGADA: "GARANTIA_AGREGADA",
  GARANTIA_ELIMINADA: "GARANTIA_ELIMINADA",

  APROBACION_SOLICITADA: "APROBACION_SOLICITADA",
  APROBACION_OTORGADA: "APROBACION_OTORGADA",
  APROBACION_RECHAZADA: "APROBACION_RECHAZADA",
  APROBACION_CONDICIONADA: "APROBACION_CONDICIONADA",

  // Errores y recuperación
  ERROR_SISTEMA: "ERROR_SISTEMA",
  TIMEOUT_SESION: "TIMEOUT_SESION",
  PROGRESO_RECUPERADO: "PROGRESO_RECUPERADO",
  PROGRESO_DESCARTADO: "PROGRESO_DESCARTADO",
}

/**
 * Calcula la duración desde el último evento
 */
const calcularDuracion = () => {
  const lastTime = sessionStorage.getItem("onboarding_last_event_time")
  const now = Date.now()
  sessionStorage.setItem("onboarding_last_event_time", now.toString())

  if (lastTime) {
    return Math.round((now - parseInt(lastTime)) / 1000)
  }
  return null
}

/**
 * Registra un evento en el funnel
 *
 * @param {string} tipoEvento - Tipo de evento (usar TIPOS_EVENTO)
 * @param {Object} datos - Datos adicionales del evento
 * @returns {Promise<Object>} Evento registrado
 */
export const registrarEvento = async (tipoEvento, datos = {}) => {
  try {
    const sesionId = obtenerSesionId()
    const duracion = calcularDuracion()

    // Obtener canal/campaña de URL params si existen
    const urlParams = new URLSearchParams(window.location.search)
    const canal =
      datos.canal || urlParams.get("utm_source") || urlParams.get("canal")
    const campania =
      datos.campania ||
      urlParams.get("utm_campaign") ||
      urlParams.get("campania")

    const payload = {
      sesionId,
      tipoEvento,
      duracionSegundos: duracion,
      canal,
      campania,
      ...datos,
    }

    const response = await axios.post(`${BASE_PATH}/evento`, payload)
    return response.data
  } catch (error) {
    console.error("Error registrando evento de funnel:", error)
    // No lanzar error para no interrumpir el flujo del usuario
    return null
  }
}

/**
 * Registra múltiples eventos en batch
 */
export const registrarEventosBatch = async eventos => {
  try {
    const sesionId = obtenerSesionId()
    const eventosConSesion = eventos.map(e => ({ ...e, sesionId }))

    await axios.post(`${BASE_PATH}/eventos/batch`, eventosConSesion)
  } catch (error) {
    console.error("Error registrando eventos en batch:", error)
  }
}

// ============================================
// Helpers para eventos comunes
// ============================================

/**
 * Registra inicio del onboarding
 */
export const trackInicioOnboarding = (canal, campania) => {
  sessionStorage.setItem("onboarding_last_event_time", Date.now().toString())
  return registrarEvento(TIPOS_EVENTO.INICIO_ONBOARDING, { canal, campania })
}

/**
 * Registra fin exitoso
 */
export const trackFinExitoso = cuit => {
  return registrarEvento(TIPOS_EVENTO.FIN_EXITOSO, { cuit })
}

/**
 * Registra abandono
 */
export const trackAbandono = (paso, nombrePaso, motivo) => {
  return registrarEvento(TIPOS_EVENTO.ABANDONO, { paso, nombrePaso, motivo })
}

/**
 * Registra cambio de paso
 */
export const trackCambioPaso = (pasoAnterior, pasoNuevo, nombrePaso) => {
  const tipoEvento =
    pasoNuevo > pasoAnterior
      ? TIPOS_EVENTO.PASO_INICIADO
      : TIPOS_EVENTO.PASO_RETROCESO

  return registrarEvento(tipoEvento, {
    paso: pasoNuevo,
    pasoAnterior,
    nombrePaso,
  })
}

/**
 * Registra paso completado
 */
export const trackPasoCompletado = (paso, nombrePaso) => {
  return registrarEvento(TIPOS_EVENTO.PASO_COMPLETADO, { paso, nombrePaso })
}

/**
 * Registra búsqueda de CUIT
 */
export const trackBusquedaCuit = (cuit, encontrado) => {
  const tipoEvento = encontrado
    ? TIPOS_EVENTO.CUIT_ENCONTRADO
    : TIPOS_EVENTO.CUIT_NO_ENCONTRADO
  return registrarEvento(tipoEvento, { cuit })
}

/**
 * Registra resultado de evaluación
 */
export const trackResultadoEvaluacion = (cuit, resultado, calificacion) => {
  const tipoEvento =
    resultado === "Aceptado"
      ? TIPOS_EVENTO.EVALUACION_COMPLETADA
      : TIPOS_EVENTO.EVALUACION_RECHAZADA
  return registrarEvento(tipoEvento, {
    cuit,
    resultado,
    datosAdicionales: { calificacion },
  })
}

/**
 * Registra alerta de KYC/Nosis
 */
export const trackAlertaRiesgo = (tipo, motivo, cuit) => {
  const tipoEvento =
    tipo === "KYC" ? TIPOS_EVENTO.KYC_ALERTA : TIPOS_EVENTO.NOSIS_ALERTA
  return registrarEvento(tipoEvento, { cuit, motivo })
}

// ============================================
// Consulta de métricas
// ============================================

/**
 * Obtiene métricas del funnel
 *
 * @param {string} fechaDesde - Fecha inicio (YYYY-MM-DD)
 * @param {string} fechaHasta - Fecha fin (YYYY-MM-DD)
 * @param {string} canal - Filtro por canal (opcional)
 * @param {string} campania - Filtro por campaña (opcional)
 */
export const obtenerMetricas = async (
  fechaDesde,
  fechaHasta,
  canal,
  campania,
) => {
  try {
    const params = new URLSearchParams({
      fechaDesde,
      fechaHasta,
      ...(canal && { canal }),
      ...(campania && { campania }),
    })

    const response = await axios.get(`${BASE_PATH}/metricas?${params}`)
    return response.data
  } catch (error) {
    console.error("Error obteniendo métricas:", error)
    throw error
  }
}

// /**
//  * Obtiene dashboard en tiempo real
//  */
// export const obtenerDashboard = async () => {
//   try {
//     const response = await axios.get(`${BASE_PATH}/dashboard`)
//     return response.data
//   } catch (error) {
//     console.error("Error obteniendo dashboard:", error)
//     throw error
//   }
// }

/**
 * Obtiene el dashboard completo de métricas del funnel
 *
 * @param {string} periodo - "hoy", "semana", "mes" (default: "semana")
 * @returns {Promise<Object>} Dashboard con métricas detalladas
 */
export const obtenerDashboard = async (periodo = "semana") => {
  try {
    const response = await axios.get(`${BASE_PATH}/dashboard`, {
      params: { periodo },
    })
    return response.data
  } catch (error) {
    console.error("Error obteniendo dashboard:", error)
    // Devolver estructura vacía para no romper el componente
    return {
      timestamp: new Date().toISOString(),
      iniciadosHoy: 0,
      completadosHoy: 0,
      abandonadosHoy: 0,
      enProgresoAhora: 0,
      variacionIniciados: null,
      variacionConversion: null,
      variacionAbandono: null,
      variacionTiempo: null,
      metricasPorPaso: [],
      tiemposPorPaso: [],
      cuellosBottella: [],
      recomendaciones: [],
      alertas: [],
      ultimosSieteDias: [],
    }
  }
}

/**
 * Obtiene métricas de hoy
 */
export const obtenerMetricasHoy = async () => {
  try {
    const response = await axios.get(`${BASE_PATH}/metricas/hoy`)
    return response.data
  } catch (error) {
    console.error("Error obteniendo métricas de hoy:", error)
    throw error
  }
}

/**
 * Obtiene métricas de la semana
 */
export const obtenerMetricasSemana = async () => {
  try {
    const response = await axios.get(`${BASE_PATH}/metricas/semana`)
    return response.data
  } catch (error) {
    console.error("Error obteniendo métricas de semana:", error)
    throw error
  }
}

/**
 * Obtiene métricas del mes
 */
export const obtenerMetricasMes = async () => {
  try {
    const response = await axios.get(`${BASE_PATH}/metricas/mes`)
    return response.data
  } catch (error) {
    console.error("Error obteniendo métricas del mes:", error)
    throw error
  }
}

/**
 * Obtiene detalle de una sesión
 */
export const obtenerDetalleSesion = async sesionId => {
  try {
    const response = await axios.get(`${BASE_PATH}/sesion/${sesionId}`)
    return response.data
  } catch (error) {
    console.error("Error obteniendo detalle de sesión:", error)
    throw error
  }
}
